import { byId, isAr, saveBlob, trackEvent } from './calc-common.js';

let cues = []; // [{ id, startMs, endMs, text }]
let nextCueId = 1;
let currentFormat = 'srt'; // 'srt' | 'vtt'
let lastActiveIndex = -1;

export function parseSrt(text) {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const blocks = normalized.split(/\n\n+/);
  const result = [];

  blocks.forEach((block, idx) => {
    const lines = block.split('\n');
    if (lines.length < 2) return;

    let timeLineIndex = 0;
    if (/^\d+$/.test(lines[0].trim())) {
      timeLineIndex = 1;
    }

    const timeLine = lines[timeLineIndex];
    if (!timeLine || !timeLine.includes('-->')) return;

    const [startStr, endStr] = timeLine.split('-->').map(s => s.trim());
    const startMs = parseTimestamp(startStr);
    const endMs = parseTimestamp(endStr);
    const cueText = lines.slice(timeLineIndex + 1).join('\n');

    result.push({
      id: idx + 1,
      startMs,
      endMs,
      text: cueText
    });
  });

  return result;
}

export function parseVtt(text) {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const lines = normalized.split('\n');
  const result = [];

  let i = 0;
  // Skip WEBVTT header
  while (i < lines.length && !lines[i].includes('-->')) {
    i++;
  }

  let cueId = 1;
  while (i < lines.length) {
    if (lines[i].includes('-->')) {
      const timeLine = lines[i];
      const [startStr, rest] = timeLine.split('-->').map(s => s.trim());
      const endStr = rest.split(/\s+/)[0]; // strip positioning attributes

      const startMs = parseTimestamp(startStr);
      const endMs = parseTimestamp(endStr);

      i++;
      const textLines = [];
      while (i < lines.length && lines[i].trim() !== '') {
        textLines.push(lines[i]);
        i++;
      }

      result.push({
        id: cueId++,
        startMs,
        endMs,
        text: textLines.join('\n')
      });
    }
    i++;
  }

  return result;
}

export function serializeSrt(cueList) {
  return cueList.map((c, i) => {
    const start = formatTimestamp(c.startMs, ',');
    const end = formatTimestamp(c.endMs, ',');
    return `${i + 1}\n${start} --> ${end}\n${c.text}\n`;
  }).join('\n');
}

export function serializeVtt(cueList) {
  const body = cueList.map((c) => {
    const start = formatTimestamp(c.startMs, '.');
    const end = formatTimestamp(c.endMs, '.');
    return `${start} --> ${end}\n${c.text}\n`;
  }).join('\n');
  return `WEBVTT\n\n${body}`;
}

function parseTimestamp(str) {
  if (!str) return 0;
  // formats: HH:MM:SS,mmm or HH:MM:SS.mmm or MM:SS.mmm
  const clean = str.replace(',', '.');
  const parts = clean.split(':');
  let h = 0, m = 0, s = 0;

  if (parts.length === 3) {
    h = parseFloat(parts[0]) || 0;
    m = parseFloat(parts[1]) || 0;
    s = parseFloat(parts[2]) || 0;
  } else if (parts.length === 2) {
    m = parseFloat(parts[0]) || 0;
    s = parseFloat(parts[1]) || 0;
  }

  return Math.round((h * 3600 + m * 60 + s) * 1000);
}

function formatTimestamp(ms, delimiter = ',') {
  const safeMs = Math.max(0, ms);
  const totalSec = Math.floor(safeMs / 1000);
  const msec = safeMs % 1000;
  const s = totalSec % 60;
  const m = Math.floor(totalSec / 60) % 60;
  const h = Math.floor(totalSec / 3600);

  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  const mmm = String(msec).padStart(3, '0');

  return `${hh}:${mm}:${ss}${delimiter}${mmm}`;
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function initSubtitleEditor() {
  const container = byId('subtitle-editor-app');
  if (!container) return;

  const dropZone = byId('sub-dropzone');
  const fileInput = byId('sub-file-input');
  const changeFileBtn = byId('sub-change-file-btn');

  // Trigger file browser and always allow re-picking same or new file
  const openFilePicker = () => {
    if (fileInput) {
      fileInput.value = '';
      fileInput.click();
    }
  };

  if (dropZone) {
    dropZone.addEventListener('click', openFilePicker);
    dropZone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openFilePicker();
      }
    });
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('dragover', 'is-dragging', 'kz-dropzone-dragover');
    });
    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('dragover', 'is-dragging', 'kz-dropzone-dragover');
    });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('dragover', 'is-dragging', 'kz-dropzone-dragover');
      if (e.dataTransfer.files?.length) {
        loadSubtitleFile(e.dataTransfer.files[0]);
      }
    });
  }

  if (changeFileBtn) {
    changeFileBtn.addEventListener('click', openFilePicker);
  }

  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      if (e.target.files?.length) {
        loadSubtitleFile(e.target.files[0]);
      }
    });
  }

  // Time Shift controls
  const shiftBtn = byId('sub-shift-btn');
  const shiftInput = byId('sub-shift-sec');
  const applyTimeShift = (sec) => {
    const shiftMs = Math.round(sec * 1000);
    if (!shiftMs) return;
    cues.forEach(c => {
      c.startMs = Math.max(0, c.startMs + shiftMs);
      c.endMs = Math.max(0, c.endMs + shiftMs);
    });
    renderCueList();
  };

  if (shiftBtn && shiftInput) {
    shiftBtn.addEventListener('click', () => {
      const shiftSec = parseFloat(shiftInput.value) || 0;
      applyTimeShift(shiftSec);
    });
  }

  // Quick Shift buttons (-1s, -0.5s, +0.5s, +1s)
  container.querySelectorAll('.sub-quick-shift-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const shiftSec = parseFloat(btn.dataset.shift) || 0;
      if (shiftInput) shiftInput.value = shiftSec.toString();
      applyTimeShift(shiftSec);
    });
  });

  // Search & Replace
  const replaceBtn = byId('sub-replace-btn');
  if (replaceBtn) {
    replaceBtn.addEventListener('click', () => {
      const searchVal = byId('sub-search-val')?.value;
      const replaceVal = byId('sub-replace-val')?.value || '';
      if (!searchVal) return;

      cues.forEach(c => {
        c.text = c.text.replaceAll(searchVal, replaceVal);
      });
      renderCueList();
    });
  }

  // Add Cue button
  const addCueBtn = byId('sub-add-cue-btn');
  if (addCueBtn) {
    addCueBtn.addEventListener('click', () => {
      const last = cues[cues.length - 1];
      const newStart = last ? last.endMs + 500 : 0;
      const newEnd = newStart + 2500;
      cues.push({
        id: nextCueId++,
        startMs: newStart,
        endMs: newEnd,
        text: isAr ? 'نص ترجمة جديد' : 'New subtitle cue'
      });
      renderCueList();
      // Scroll to new row at the bottom
      setTimeout(() => {
        const tbody = byId('sub-cues-body');
        const lastRow = tbody?.querySelector('tr:last-child');
        if (lastRow) {
          lastRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          const ta = lastRow.querySelector('.cue-text');
          if (ta) ta.focus();
        }
      }, 50);
    });
  }

  // Local Video preview input with container & chosen badge
  const videoInput = byId('sub-video-input');
  const videoEl = byId('sub-video-player');
  const videoContainer = byId('sub-video-container');

  if (videoInput && videoEl) {
    videoInput.addEventListener('change', (e) => {
      if (e.target.files?.[0]) {
        const file = e.target.files[0];
        if (videoEl.src) {
          URL.revokeObjectURL(videoEl.src);
        }
        videoEl.src = URL.createObjectURL(file);
        if (videoContainer) videoContainer.style.display = 'block';
        videoEl.style.display = 'block';
        const badge = byId('sub-video-badge');
        const filenameSpan = byId('sub-video-filename');
        if (filenameSpan) filenameSpan.textContent = file.name;
        if (badge) badge.style.display = 'inline-flex';
        syncVideoSubtitle();
      }
    });

    const removeVideoBtn = byId('sub-video-clear-btn');
    if (removeVideoBtn) {
      removeVideoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (videoEl) {
          videoEl.pause();
          if (videoEl.src) URL.revokeObjectURL(videoEl.src);
          videoEl.src = '';
          videoEl.style.display = 'none';
        }
        if (videoContainer) videoContainer.style.display = 'none';
        if (videoInput) videoInput.value = '';
        const badge = byId('sub-video-badge');
        if (badge) badge.style.display = 'none';
        const overlay = byId('sub-video-overlay');
        if (overlay) overlay.style.display = 'none';

        // Clear active cue highlighting
        if (lastActiveIndex !== -1) {
          const tbody = byId('sub-cues-body');
          const prevRow = tbody?.querySelector(`tr[data-idx="${lastActiveIndex}"]`);
          if (prevRow) prevRow.classList.remove('is-active-cue');
          lastActiveIndex = -1;
        }
      });
    }

    const syncVideoSubtitle = () => {
      if (!videoEl || !videoEl.src) return;
      const currentMs = Math.round(videoEl.currentTime * 1000);
      const activeIndex = cues.findIndex(c => currentMs >= c.startMs && currentMs <= c.endMs);
      
      const overlay = byId('sub-video-overlay');
      if (overlay) {
        if (activeIndex !== -1) {
          overlay.textContent = cues[activeIndex].text;
          overlay.style.display = 'block';
        } else {
          overlay.textContent = '';
          overlay.style.display = 'none';
        }
      }

      if (activeIndex !== lastActiveIndex) {
        const tbody = byId('sub-cues-body');
        if (tbody) {
          if (lastActiveIndex !== -1) {
            const prevRow = tbody.querySelector(`tr[data-idx="${lastActiveIndex}"]`);
            if (prevRow) prevRow.classList.remove('is-active-cue');
          }
          if (activeIndex !== -1) {
            const activeRow = tbody.querySelector(`tr[data-idx="${activeIndex}"]`);
            if (activeRow) {
              activeRow.classList.add('is-active-cue');
              activeRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }
        lastActiveIndex = activeIndex;
      }
    };

    videoEl.addEventListener('timeupdate', syncVideoSubtitle);
    videoEl.addEventListener('seeked', syncVideoSubtitle);
  }

  // Export buttons
  const exportSrtBtn = byId('sub-export-srt');
  const exportVttBtn = byId('sub-export-vtt');

  if (exportSrtBtn) exportSrtBtn.addEventListener('click', () => exportSubtitles('srt'));
  if (exportVttBtn) exportVttBtn.addEventListener('click', () => exportSubtitles('vtt'));
}

function loadSubtitleFile(file) {
  trackEvent('tool_started', 'subtitle-editor');
  const isVtt = file.name.toLowerCase().endsWith('.vtt');
  currentFormat = isVtt ? 'vtt' : 'srt';
  lastActiveIndex = -1;

  const reader = new FileReader();
  reader.onload = (e) => {
    const content = e.target.result;
    cues = isVtt ? parseVtt(content) : parseSrt(content);
    nextCueId = cues.length + 1;

    const editorWrap = byId('sub-editor-wrap');
    const dropzone = byId('sub-dropzone');
    if (editorWrap) editorWrap.style.display = 'block';

    // Update loaded file information in pill and compact dropzone
    const filenameEl = byId('sub-loaded-filename');
    if (filenameEl) filenameEl.textContent = file.name;

    const formatBadge = byId('sub-format-badge');
    if (formatBadge) formatBadge.textContent = currentFormat.toUpperCase();

    const cuesCountBadge = byId('sub-cues-count-badge');
    if (cuesCountBadge) cuesCountBadge.textContent = `${cues.length} ${isAr ? 'مقطع' : 'cues'}`;

    if (dropzone) {
      dropzone.classList.add('is-compact');
      dropzone.style.display = 'flex';
      const titleEl = dropzone.querySelector('.dropzone-title');
      const subEl = dropzone.querySelector('.dropzone-subtitle');
      if (titleEl) titleEl.textContent = isAr ? 'انقر أو اسحب لاختيار ملف ترجمة مختلف' : 'Click or drop to choose a different subtitle file';
      if (subEl) subEl.textContent = `${file.name} · (${cues.length} ${isAr ? 'مقطع' : 'cues'})`;
    }

    renderCueList();
  };
  reader.readAsText(file, 'utf-8');
}

function renderCueList() {
  const tbody = byId('sub-cues-body');
  if (!tbody) return;

  // Update cues count badge
  const cuesCountBadge = byId('sub-cues-count-badge');
  if (cuesCountBadge) cuesCountBadge.textContent = `${cues.length} ${isAr ? 'مقطع' : 'cues'}`;

  // Check overlaps
  let overlapCount = 0;
  for (let i = 0; i < cues.length - 1; i++) {
    if (cues[i].endMs > cues[i + 1].startMs) {
      overlapCount++;
    }
  }

  const badge = byId('sub-overlap-badge');
  if (badge) {
    if (overlapCount > 0) {
      badge.textContent = isAr ? `يوجد ${overlapCount} تداخل زمني!` : `${overlapCount} timing overlaps detected!`;
      badge.style.display = 'inline-block';
    } else {
      badge.style.display = 'none';
    }
  }

  tbody.innerHTML = cues.map((cue, idx) => {
    const isOverlapping = idx < cues.length - 1 && cue.endMs > cues[idx + 1].startMs;
    const isNegative = cue.endMs < cue.startMs;
    const isActive = idx === lastActiveIndex;

    const rowClasses = [
      'sub-cue-row',
      isOverlapping || isNegative ? 'sub-cue-row-error' : '',
      isActive ? 'is-active-cue' : ''
    ].filter(Boolean).join(' ');

    return `
      <tr class="${rowClasses}" data-idx="${idx}">
        <td class="sub-cue-index" style="width:48px; text-align:center;">
          ${idx + 1}
        </td>
        <td style="width:140px;">
          <input type="text" class="sub-time-input cue-start" value="${formatTimestamp(cue.startMs, ',')}" aria-label="${isAr ? 'وقت البداية' : 'Start time'}">
        </td>
        <td style="width:140px;">
          <input type="text" class="sub-time-input cue-end" value="${formatTimestamp(cue.endMs, ',')}" aria-label="${isAr ? 'وقت النهاية' : 'End time'}">
        </td>
        <td class="sub-cue-content">
          <textarea class="sub-text-textarea cue-text" rows="2" aria-label="${isAr ? 'نص الترجمة' : 'Subtitle text'}">${escapeHtml(cue.text)}</textarea>
        </td>
        <td style="width:150px; text-align:center;">
          <div class="sub-cue-actions">
            <button type="button" class="sub-action-btn cue-play-btn" title="${isAr ? 'تشغيل الفيديو من هذا التوقيت' : 'Play video from this timestamp'}" aria-label="Play">
              <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>
            </button>
            <button type="button" class="sub-action-btn cue-split-btn" title="${isAr ? 'تقسيم المقطع' : 'Split cue'}" aria-label="Split">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></svg>
            </button>
            <button type="button" class="sub-action-btn cue-merge-btn" title="${isAr ? 'دمج مع التالي' : 'Merge with next'}" aria-label="Merge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 9l4-4 4 4"/><path d="M12 5v14"/><path d="M16 15l-4 4-4-4"/></svg>
            </button>
            <button type="button" class="sub-action-btn sub-action-btn-danger cue-del-btn" title="${isAr ? 'حذف المقطع' : 'Delete cue'}" aria-label="Delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  // Event handlers
  tbody.querySelectorAll('.cue-start').forEach((inp, i) => {
    inp.addEventListener('change', (e) => {
      cues[i].startMs = parseTimestamp(e.target.value);
      renderCueList();
    });
  });

  tbody.querySelectorAll('.cue-end').forEach((inp, i) => {
    inp.addEventListener('change', (e) => {
      cues[i].endMs = parseTimestamp(e.target.value);
      renderCueList();
    });
  });

  tbody.querySelectorAll('.cue-text').forEach((txt, i) => {
    txt.addEventListener('input', (e) => {
      cues[i].text = e.target.value;
    });
  });

  tbody.querySelectorAll('.cue-play-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      const videoEl = byId('sub-video-player');
      if (videoEl && videoEl.src) {
        videoEl.currentTime = cues[i].startMs / 1000;
        videoEl.play();
      } else {
        const videoInput = byId('sub-video-input');
        if (videoInput) videoInput.click();
      }
    });
  });

  tbody.querySelectorAll('.cue-split-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      const c = cues[i];
      const midMs = Math.round((c.startMs + c.endMs) / 2);
      const words = c.text.split(/\s+/);
      const half = Math.ceil(words.length / 2);
      const firstText = words.slice(0, half).join(' ');
      const secondText = words.slice(half).join(' ');

      cues.splice(i, 1,
        { id: nextCueId++, startMs: c.startMs, endMs: midMs, text: firstText },
        { id: nextCueId++, startMs: midMs, endMs: c.endMs, text: secondText }
      );
      renderCueList();
    });
  });

  tbody.querySelectorAll('.cue-merge-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      if (i >= cues.length - 1) return;
      const cur = cues[i];
      const nxt = cues[i + 1];
      cur.endMs = nxt.endMs;
      cur.text = `${cur.text}\n${nxt.text}`.trim();
      cues.splice(i + 1, 1);
      renderCueList();
    });
  });

  tbody.querySelectorAll('.cue-del-btn').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      cues.splice(i, 1);
      if (lastActiveIndex === i) lastActiveIndex = -1;
      else if (lastActiveIndex > i) lastActiveIndex--;
      renderCueList();
    });
  });
}

function exportSubtitles(format) {
  const text = format === 'vtt' ? serializeVtt(cues) : serializeSrt(cues);
  const blob = new Blob([text], { type: format === 'vtt' ? 'text/vtt;charset=utf-8' : 'text/plain;charset=utf-8' });
  saveBlob(blob, `subtitles.${format}`);
  trackEvent('tool_completed', 'subtitle-editor', { format, cues_count: cues.length });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubtitleEditor);
  } else {
    initSubtitleEditor();
  }
}
